import { DomainUpsertLoadingForm } from "@/components/domain";
import { useSheetContext } from "@/context/sheet";
import { useGetAgency } from "@/hooks/agencies";
import { useCreateTourAgency } from "@/hooks/tour-agencies";
import { CreateTourAgency } from "@/models";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Form } from "./-form";

export const Route = createFileRoute(
  "/(tour-agencies)/tour-agencies/$id/_sheet/new"
)({
  component: RouteComponent,
  validateSearch: z.object({
    domain: z.enum(["agency", "tour"]),
  }),
});

function RouteComponent() {
  const { domain } = Route.useSearch();
  const { id } = Route.useParams();
  const { close } = useSheetContext();
  const navigate = useNavigate();
  const { mutateAsync } = useCreateTourAgency(domain, id, {
    onSuccess: () => {
      close(() => {
        navigate({
          to: "/tour-agencies/$id",
          params: { id },
          search: { domain },
        });
      });
    },
  });

  const create = async (tourAgency: CreateTourAgency) => {
    await mutateAsync(tourAgency);
  };

  if (domain === "agency") return <AgencyForm onCreate={create} id={id} />;
  return <TourForm onCreate={create} id={id} />;
}

function AgencyForm({
  onCreate,
  id,
}: {
  id: string;
  onCreate: (tourAgency: CreateTourAgency) => Promise<void>;
}) {
  const { data: agency, isLoading: agencyLoading } = useGetAgency(id);

  if (agencyLoading || !agency) return <DomainUpsertLoadingForm />;
  return (
    <Form
      onUpsert={onCreate}
      domain="agency"
      domainId={id}
      defaultCommissions={{
        bikesCommissions: agency.bikesCommissions,
        extraNightsCommissions: agency.extraNightsCommissions,
        individualRoomSupplementCommission:
          agency.individualRoomSupplementCommission,
        priceCommission: agency.priceCommission,
        roomChangeCommissions: agency.roomChangeCommissions,
        seasonSupplementCommission: agency.seasonSupplementCommission,
        servicesCommissions: agency.servicesCommissions,
        soloTravelerCommission: agency.soloTravelerCommission,
      }}
    />
  );
}

function TourForm({
  onCreate,
  id,
}: {
  id: string;
  onCreate: (tourAgency: CreateTourAgency) => Promise<void>;
}) {
  return <Form onUpsert={onCreate} domain="tour" domainId={id} />;
}
